/**
 * 设备故障报警看板
 * 数据源复用翻车机报警处理页：/api/produce/device-alarm-records/wagontippler
 */
import request from '@/router/axios'

const WAGON_TIPPLER_URL = '/api/produce/device-alarm-records/wagontippler'
const DEFAULT_PAGE_SIZE = 50

const PROCESS_STEPS = [
  { key: 'CONFIRM', name: '待分派' },
  { key: 'DISPATCH', name: '处置中' },
  { key: 'REPAIR', name: '待验收' },
  { key: 'VERIFY', name: '已归档' }
]

function unwrap(res) {
  const body = res && res.data !== undefined ? res.data : res
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body || {}
}

function valueText(value, fallback = '--') {
  if (value === null || value === undefined || value === '') return fallback
  return String(value)
}

function alarmLevelText(row = {}) {
  const value = valueText(row.alarmLevel || row.alertType, '')
  const label = valueText(row.alarmLevelName, '')
  if (label.indexOf('紧急') > -1 || value === '3') return '紧急'
  if (label.indexOf('一级') > -1) return '一级预警'
  if (label.indexOf('二级') > -1 || value === '2' || label.indexOf('一般') > -1) return '二级预警'
  if (label.indexOf('三级') > -1 || value === '1' || label.indexOf('预警') > -1) return '三级预警'
  return label || '三级预警'
}

function processStatus(row = {}, timeline = []) {
  if (String(row.alertStatus) === '4') return '已归档'
  if (String(row.alertStatus) === '3') return '待验收'
  if (String(row.alertStatus) === '2' || timeline.length > 0) return '处置中'
  return '待分派'
}

function stepIndex(step) {
  return PROCESS_STEPS.findIndex(item => item.key === step)
}

function buildStages(row = {}, timeline = []) {
  const doneSteps = timeline
    .map(item => item.processStep || item.step)
    .filter(Boolean)
  const latestStep = doneSteps.length
    ? doneSteps[doneSteps.length - 1]
    : (row.latestProcessStep || row.latestStep || row.processStep || '')
  const latestIndex = stepIndex(latestStep)
  const nextIndex = latestIndex >= 0 ? latestIndex + 1 : 0

  return PROCESS_STEPS.map((step, index) => {
    const process = timeline.find(item => (item.processStep || item.step) === step.key) || {}
    let status = 'pending'
    if (doneSteps.indexOf(step.key) > -1) status = 'done'
    else if (index === nextIndex && String(row.alertStatus) !== '4') status = 'active'
    return {
      name: step.name,
      handler: valueText(process.processorName || process.handler || row.processorName, '--'),
      receiveTime: process.processTime || row.processTime || null,
      completeTime: process.processTime || null,
      measure: valueText(process.processContent || process.content || row.processResult || row.alarmContent, ''),
      status
    }
  })
}

function buildTraces(row = {}, timeline = []) {
  if (timeline.length) {
    return timeline.map(item => ({
      time: valueText(item.processTime || item.createTime, '--'),
      operator: valueText(item.processorName || item.handler, '--'),
      action: valueText(item.processContent || item.content || item.processStepName || item.processStep, '处理节点'),
      remark: valueText(item.remark, '')
    }))
  }
  if (row.processTime || row.processResult || row.processorName) {
    return [{
      time: valueText(row.processTime || row.alertTime || row.createTime, '--'),
      operator: valueText(row.processorName || row.createName, '--'),
      action: valueText(row.processResult || row.alarmContent, '报警处理'),
      remark: valueText(row.remark, '')
    }]
  }
  return []
}

function mapAlarmRecord(row = {}, timeline = []) {
  const id = valueText(row.id, '')
  return {
    id,
    area: valueText(row.category || row.deviceType, '翻车机区域'),
    type: valueText(row.abnormalType || row.type || row.deviceType, '设备报警'),
    desc: valueText(row.alarmContent || row.deviceName, '翻车机报警'),
    reporter: valueText(row.processorName || row.createName, '--'),
    reporterId: valueText(row.processorId || row.createUser, '--'),
    reportTime: valueText(row.alertTime || row.createTime, '--'),
    urgency: alarmLevelText(row),
    process: {
      status: processStatus(row, timeline),
      stages: buildStages(row, timeline)
    },
    traces: buildTraces(row, timeline),
    detailLoaded: timeline.length > 0,
    raw: row
  }
}

/**
 * @param {object} params 翻车机报警查询参数，默认加载前 50 条
 * @returns {Promise<{hazards: array, total: number}>}
 */
export function fetchEquipmentAlarm(params = {}) {
  const data = {
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    ...params
  }
  return request({
    url: `${WAGON_TIPPLER_URL}/select/page`,
    method: 'post',
    data
  }).then(res => {
    const pageData = unwrap(res)
    const records = Array.isArray(pageData) ? pageData : (pageData.records || [])
    return {
      hazards: records.map(item => mapAlarmRecord(item)),
      total: Number(pageData.total || records.length || 0)
    }
  })
}

export function fetchEquipmentAlarmDetail(id) {
  return request({
    url: `${WAGON_TIPPLER_URL}/detail`,
    method: 'get',
    params: { id }
  }).then(res => {
    const detail = unwrap(res)
    const alarm = detail.alarm || detail.record || detail.detail || {}
    const timeline = detail.timeline || detail.processSteps || detail.steps || detail.processList || []
    return mapAlarmRecord(alarm, timeline)
  })
}
